import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Users, FileCheck2, DollarSign, Loader2 } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { TOOL_LIST } from "@/lib/tools";
import { SalesCustomers } from "@/components/SalesCustomers";

const Stat = ({ icon: Icon, label, value, sub, color, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-6">
    <div className="flex items-center justify-between">
      <p className="font-mono text-[11px] uppercase tracking-wider text-slate-400">{label}</p>
      <span className="grid h-9 w-9 place-items-center rounded-lg text-white" style={{ background: color }}><Icon className="h-4 w-4" /></span>
    </div>
    <p className="mt-4 font-display text-4xl font-bold text-slate-900" data-testid={testId}>{value}</p>
    <p className="mt-1 text-sm text-slate-500">{sub}</p>
  </div>
);

const DailyChart = ({ data, dataKey, color, title, fmt }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-6">
    <p className="text-sm font-semibold text-slate-900">{title}</p>
    <div className="mt-4 h-56">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid vertical={false} stroke="#f1f5f9" />
          <XAxis dataKey="date" interval={4} tickFormatter={(d) => d.slice(8)} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={36} />
          <Tooltip formatter={(v) => fmt(v)} cursor={{ fill: "#f8fafc" }} />
          <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const ToolBreakdown = ({ byTool, total }) => {
  const { t } = useLang();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <p className="text-sm font-semibold text-slate-900">{t("owner.byTool")}</p>
      <ul className="mt-5 space-y-4">
        {TOOL_LIST.map((tl) => {
          const n = byTool[tl.key] || 0;
          return (
            <li key={tl.key} data-testid={`owner-tool-${tl.key}`}>
              <div className="flex justify-between text-sm"><span className="text-slate-700">{t(`tools.${tl.slug}.name`)}</span><span className="font-mono font-semibold">{n}</span></div>
              <div className="mt-1.5 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full" style={{ width: `${total ? (n / total) * 100 : 0}%`, background: tl.color }} /></div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const Table = ({ title, rows, cols, empty, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={testId}>
    <p className="text-sm font-semibold text-slate-900">{title}</p>
    {rows.length ? (
      <table className="mt-4 w-full text-sm">
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => <tr key={i}>{cols.map((c, j) => <td key={j} className={`py-2.5 ${j ? "text-right font-mono text-xs text-slate-500" : "truncate text-slate-700"}`}>{c(r)}</td>)}</tr>)}
        </tbody>
      </table>
    ) : <p className="mt-4 text-sm text-slate-400">{empty}</p>}
  </div>
);

export default function Owner() {
  const { user } = useAuth();
  const { t, locale } = useLang();
  const [s, setS] = useState(null);
  useEffect(() => { if (user?.is_owner) api.get("/owner/stats").then(({ data }) => setS(data)).catch(() => setS(false)); }, [user]);
  if (!user.is_owner) return <Navigate to="/painel" replace />;
  if (s === false) return <p className="py-32 text-center text-sm text-red-500" data-testid="owner-error">{t("errors.generic")}</p>;
  if (!s) return <div className="grid min-h-[60vh] place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const usd = (v) => new Intl.NumberFormat(locale, { style: "currency", currency: "USD" }).format(v);
  const date = (iso) => new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "short" });

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8" data-testid="owner-page">
      <p className="font-mono text-xs uppercase tracking-wider text-slate-400">{t("owner.eyebrow")}</p>
      <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{t("owner.title")}</h1>
      <div className="mt-10 grid gap-5 md:grid-cols-3">
        <Stat icon={Users} color="#00A3E0" label={t("owner.users")} value={s.users.total} testId="owner-users-total"
          sub={t("owner.usersSub", { d7: s.users.last_7, d30: s.users.last_30 })} />
        <Stat icon={FileCheck2} color="#E40046" label={t("owner.files")} value={s.files.total} testId="owner-files-total"
          sub={t("owner.filesSub", { d7: s.files.last_7, f: s.files.failed })} />
        <Stat icon={DollarSign} color="#0F172A" label={t("owner.sales")} value={usd(s.sales.revenue)} testId="owner-revenue"
          sub={t("owner.salesSub", { n: s.sales.count, c: s.sales.credits_sold, b: s.users.buyers })} />
      </div>
      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <DailyChart data={s.daily} dataKey="jobs" color="#E40046" title={t("owner.chartFiles")} fmt={(v) => v} />
        <DailyChart data={s.daily} dataKey="revenue" color="#00A3E0" title={t("owner.chartRevenue")} fmt={usd} />
      </div>
      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        <ToolBreakdown byTool={s.files.by_tool} total={s.files.total} />
        <Table testId="owner-recent-sales" title={t("owner.recentSales")} rows={s.recent_sales} empty={t("owner.noSales")}
          cols={[(r) => r.email, (r) => usd(r.amount), (r) => date(r.created_at)]} />
        <Table testId="owner-recent-users" title={t("owner.recentUsers")} rows={s.recent_users} empty="-"
          cols={[(r) => r.email, (r) => date(r.created_at)]} />
      </div>
      <SalesCustomers />
    </div>
  );
}
